import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, ArrowRight, Check, Loader2, SkipForward, Sparkles, Info, Lightbulb, Save,
  RefreshCw, Minimize2, Maximize2, Wand2, Download, History, Pencil, FileText,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function ResultView({ wf, docId, initialContent, onBack }) {
  const [content, setContent] = useState(initialContent || "");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState("");
  const [versions, setVersions] = useState(null);

  const transform = async (action, tone) => {
    setBusy(action);
    try {
      const { data } = await api.post(`/workflows/documents/${docId}/transform`, { action, tone });
      setContent(data.content); toast.success("Updated");
    } catch (e) { toast.error(e.response?.status === 402 ? "AI limit reached — your document is saved." : (formatApiError(e.response?.data?.detail) || "Could not update")); }
    finally { setBusy(""); }
  };
  const regenerate = async () => {
    setBusy("regen");
    try { const { data } = await api.post(`/workflows/${wf}/generate`, { regenerate: true }); setContent(data.content); toast.success("Regenerated"); }
    catch (e) { toast.error(e.response?.status === 402 ? "Document limit reached." : "Could not regenerate"); }
    finally { setBusy(""); }
  };
  const saveEdit = async () => {
    setBusy("save");
    try { await api.put(`/workflows/documents/${docId}`, { content }); setEditing(false); toast.success("Saved a new version"); }
    catch (e) { toast.error("Could not save"); } finally { setBusy(""); }
  };
  const loadVersions = async () => {
    try { const { data } = await api.get(`/workflows/documents/${docId}/versions`); setVersions(data.versions); }
    catch (e) { toast.error("Could not load versions"); }
  };
  const download = (fmt) => {
    const url = `${BACKEND}/api/workflows/documents/${docId}/export?format=${fmt}`;
    window.open(url, "_blank");
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="workflow-result">
      <div className="flex items-center justify-between mb-4">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-[#A1A1AA] hover:text-white"><ArrowLeft className="w-4 h-4" /> Workflows</button>
        <div className="flex items-center gap-1.5 text-emerald-300 text-sm"><Check className="w-4 h-4" /> Generated & saved to Documents</div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <Button onClick={() => setEditing(!editing)} data-testid="result-edit" variant="outline" className="rounded-full h-9 text-xs border-white/15 text-[#D4D4D8]"><Pencil className="w-3.5 h-3.5 mr-1" /> {editing ? "Preview" : "Edit"}</Button>
        <Button onClick={regenerate} disabled={!!busy} data-testid="result-regenerate" variant="outline" className="rounded-full h-9 text-xs border-white/15 text-[#D4D4D8]">{busy === "regen" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />} Regenerate</Button>
        <Button onClick={() => transform("shorten")} disabled={!!busy} data-testid="result-shorten" variant="outline" className="rounded-full h-9 text-xs border-white/15 text-[#D4D4D8]"><Minimize2 className="w-3.5 h-3.5 mr-1" /> Shorten</Button>
        <Button onClick={() => transform("expand")} disabled={!!busy} data-testid="result-expand" variant="outline" className="rounded-full h-9 text-xs border-white/15 text-[#D4D4D8]"><Maximize2 className="w-3.5 h-3.5 mr-1" /> Expand</Button>
        <Button onClick={() => transform("tone", "professional")} disabled={!!busy} data-testid="result-tone" variant="outline" className="rounded-full h-9 text-xs border-white/15 text-[#D4D4D8]"><Wand2 className="w-3.5 h-3.5 mr-1" /> Change tone</Button>
        <Button onClick={loadVersions} data-testid="result-versions" variant="outline" className="rounded-full h-9 text-xs border-white/15 text-[#D4D4D8]"><History className="w-3.5 h-3.5 mr-1" /> Versions</Button>
        <Button onClick={() => download("pdf")} data-testid="result-export-pdf" className="rounded-full h-9 text-xs fv-gold-gradient text-[#0A0A0B] font-semibold"><Download className="w-3.5 h-3.5 mr-1" /> PDF</Button>
        <Button onClick={() => download("docx")} data-testid="result-export-docx" className="rounded-full h-9 text-xs fv-gold-gradient text-[#0A0A0B] font-semibold"><Download className="w-3.5 h-3.5 mr-1" /> Word</Button>
      </div>

      {busy && busy !== "save" && <div className="text-xs text-[#D4AF37] mb-2 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Working…</div>}

      {editing ? (
        <>
          <Textarea value={content} onChange={(e) => setContent(e.target.value)} data-testid="result-editor"
            className="bg-[#0d0d0f] border-white/10 text-white text-sm min-h-[420px] font-mono" />
          <Button onClick={saveEdit} disabled={busy === "save"} data-testid="result-save" className="mt-3 fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full"><Save className="w-4 h-4 mr-1.5" /> Save version</Button>
        </>
      ) : (
        <div className="rounded-2xl border border-white/8 bg-[#121214] p-6 text-sm text-[#D4D4D8] whitespace-pre-wrap leading-relaxed" data-testid="result-content">{content}</div>
      )}

      {versions && (
        <div className="mt-4 rounded-2xl border border-white/8 bg-[#121214] p-4" data-testid="result-version-list">
          <div className="text-white font-medium mb-2 text-sm">Version history ({versions.length})</div>
          {versions.map((v, i) => (
            <details key={i} className="border-b border-white/5 py-2 last:border-0">
              <summary className="cursor-pointer text-sm text-[#A1A1AA] hover:text-white">{v.label}</summary>
              <div className="text-xs text-[#71717A] whitespace-pre-wrap mt-2 max-h-40 overflow-y-auto">{v.content}</div>
            </details>
          ))}
        </div>
      )}
      <p className="text-[10px] text-[#52525B] mt-4">AI-generated educational content — review and edit before relying on it. Not legal, tax, credit, or funding advice.</p>
    </div>
  );
}

export default function WorkflowRunner() {
  const { wf } = useParams();
  const navigate = useNavigate();
  const [defn, setDefn] = useState(null);
  const [session, setSession] = useState(null);
  const [step, setStep] = useState(0);
  const [value, setValue] = useState("");
  const [custom, setCustom] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null); // {docId, content}

  useEffect(() => {
    api.get(`/workflows/${wf}/session`).then(({ data }) => {
      setDefn(data.definition); setSession(data.session);
      if (data.session.status === "completed" && data.session.result_doc_id) {
        api.get(`/workflows/documents/${data.session.result_doc_id}`).then((r) => setResult({ docId: data.session.result_doc_id, content: r.data.content })).catch(() => {});
      }
      setStep(Math.min(data.session.current_step || 0, data.definition.steps.length - 1));
    }).catch(() => { toast.error("Workflow not found"); navigate("/workflows"); });
  }, [wf, navigate]);

  const steps = defn?.steps || [];
  const q = steps[step];
  const answers = session?.answers || {};

  useEffect(() => {
    if (!q) return;
    const existing = answers[q.key] || "";
    setValue(existing);
    setCustom(existing ? (q.options ? !q.options.includes(existing) : false) : false);
  }, [step, q]); // eslint-disable-line

  const persist = useCallback(async (key, val, nextStep) => {
    setSession((s) => ({ ...s, answers: { ...s.answers, [key]: val } }));
    try { await api.put(`/workflows/${wf}/answer`, { key, value: val, current_step: nextStep }); } catch (e) { /* autosave */ }
  }, [wf]);

  const canAdvance = q && (q.optional || String(value).trim().length > 0);

  const next = async () => {
    if (!q.optional && !String(value).trim()) { toast.error("Please answer to continue"); return; }
    setSaving(true); await persist(q.key, String(value).trim(), Math.min(step + 1, steps.length - 1)); setSaving(false);
    if (step + 1 >= steps.length) return generate();
    setStep(step + 1);
  };
  const skip = async () => { if (!q?.optional) return; await persist(q.key, "", Math.min(step + 1, steps.length - 1)); if (step + 1 >= steps.length) return generate(); setStep(step + 1); };
  const back = () => step > 0 && setStep(step - 1);
  const saveExit = async () => { await persist(q.key, String(value).trim(), step); toast.success("Progress saved — resume anytime"); navigate("/workflows"); };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/workflows/${wf}/generate`, {});
      setResult({ docId: data.doc_id, content: data.content });
      toast.success("Your result is ready!");
    } catch (e) {
      const status = e.response?.status;
      if (status === 402) toast.error("You've reached your monthly document limit — upgrade to generate more.");
      else if (status === 429) toast.error("Please slow down a moment and try again.");
      else if (status === 400) toast.error(formatApiError(e.response?.data?.detail) || "Please answer a few questions first.");
      else toast.error("Generation failed — please try again.");
    } finally { setGenerating(false); }
  };

  if (!defn) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" /></div>;
  if (result) return <ResultView wf={wf} docId={result.docId} initialContent={result.content} onBack={() => navigate("/workflows")} />;
  if (generating) return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center" data-testid="workflow-generating">
      <div className="w-14 h-14 rounded-2xl fv-gold-gradient flex items-center justify-center mb-4 animate-pulse"><Sparkles className="w-7 h-7 text-[#0A0A0B]" /></div>
      <h2 className="font-heading text-2xl text-white mb-1">Generating your {defn.name.toLowerCase()}…</h2>
      <p className="text-sm text-[#A1A1AA]">The AI coach is putting it together from your answers.</p>
    </div>
  );

  const total = steps.length;
  const pct = Math.round((step / total) * 100);

  return (
    <div className="fv-fade-up max-w-2xl mx-auto" data-testid="workflow-runner">
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => navigate("/workflows")} className="flex items-center gap-1.5 text-sm text-[#A1A1AA] hover:text-white"><ArrowLeft className="w-4 h-4" /> Workflows</button>
        <button onClick={saveExit} data-testid="workflow-save-exit" className="flex items-center gap-1.5 text-sm text-[#71717A] hover:text-white"><Save className="w-4 h-4" /> Save & exit</button>
      </div>

      <div className="flex items-center justify-between mb-2 text-xs">
        <span className="text-[#D4AF37] tracking-[0.15em] uppercase flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> {defn.name}</span>
        <span className="text-[#71717A]" data-testid="workflow-progress">Step {step + 1} of {total} · {pct}%</span>
      </div>
      <div className="h-1.5 bg-white/5 rounded-full mb-8 overflow-hidden"><motion.div className="h-full fv-gold-gradient" animate={{ width: `${pct}%` }} transition={{ duration: 0.4 }} /></div>

      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.25 }}>
          <h1 className="font-heading text-2xl sm:text-3xl text-white mb-3" data-testid="workflow-question">{q.question}</h1>
          {q.why && <div className="flex items-start gap-2 text-xs text-[#D4AF37]/90 mb-2" data-testid="workflow-why"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span><b>Why this matters:</b> {q.why}</span></div>}
          {q.example && <div className="flex items-start gap-2 text-xs text-[#71717A] mb-5"><Lightbulb className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span>Example: {q.example}</span></div>}

          {(q.type === "select" || q.type === "select_or_custom") && !custom && (
            <div className="grid sm:grid-cols-2 gap-2.5 mb-4" data-testid="workflow-options">
              {q.options.map((opt) => (
                <button key={opt} onClick={() => setValue(opt)} data-testid={`workflow-option-${opt.slice(0, 10).replace(/\s/g, "-").toLowerCase()}`}
                  className={`text-left px-4 py-3 rounded-xl border text-sm transition-colors ${value === opt ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white" : "border-white/10 text-[#D4D4D8] hover:border-white/25"}`}>
                  {value === opt && <Check className="w-4 h-4 text-[#D4AF37] inline mr-2" />}{opt}
                </button>
              ))}
            </div>
          )}
          {q.type === "select_or_custom" && (
            <button onClick={() => { setCustom(!custom); setValue(""); }} className="text-xs text-[#D4AF37] hover:underline mb-4">{custom ? "← Choose from list" : "Enter my own answer"}</button>
          )}
          {(q.type === "text" || ((q.type === "select_or_custom") && custom)) && (
            <Input autoFocus value={value} onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => e.key === "Enter" && canAdvance && next()}
              placeholder={q.example || "Type your answer"} data-testid="workflow-input" className="bg-[#121214] border-white/10 text-white h-12 text-base mb-4" />
          )}
          {q.type === "textarea" && (
            <Textarea autoFocus value={value} onChange={(e) => setValue(e.target.value)} placeholder={q.example || "Type your answer"} data-testid="workflow-input"
              className="bg-[#121214] border-white/10 text-white text-base min-h-[120px] mb-4" />
          )}

          <div className="flex items-center justify-between mt-6">
            <button onClick={back} disabled={step === 0} data-testid="workflow-back" className="flex items-center gap-1.5 text-sm text-[#A1A1AA] hover:text-white disabled:opacity-30"><ArrowLeft className="w-4 h-4" /> Back</button>
            <div className="flex items-center gap-3">
              {q.optional && <button onClick={skip} data-testid="workflow-skip" className="flex items-center gap-1.5 text-sm text-[#71717A] hover:text-white"><SkipForward className="w-4 h-4" /> Skip</button>}
              <Button onClick={next} disabled={saving || !canAdvance} data-testid="workflow-next" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full px-7 h-11">
                {step + 1 >= total ? "Generate" : "Continue"} <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          </div>
          <p className="text-[10px] text-[#52525B] mt-4 flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-400" /> Auto-saved — leave and resume anytime.</p>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
